import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button, Card, Tag, Tooltip } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import Table from "../../general/table-components/table";
import SkipAnalyticsContext from "../../../context/skip-analytics/context";
import { IDriverSkipStats } from "../../../models/skip-analytics/response";
import styles from "./styles.module.scss";

const SkipDriversTable = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { driversStats, driversQuery, loading, actions } =
    useContext(SkipAnalyticsContext);

  const columns: ColumnsType<IDriverSkipStats> = [
    {
      title: t("driver_id"),
      dataIndex: "driverId",
      key: "driverId",
      width: 80,
      sorter: (a, b) => a.driverId - b.driverId,
    },
    {
      title: t("driver_name"),
      dataIndex: "driverName",
      key: "driverName",
      ellipsis: true,
    },
    {
      title: t("phone"),
      dataIndex: "driverPhone",
      key: "driverPhone",
      ellipsis: true,
      render: (phone) => phone || "-",
    },
    {
      title: t("total_skips"),
      dataIndex: "totalSkips",
      key: "totalSkips",
      width: 100,
      sorter: (a, b) => a.totalSkips - b.totalSkips,
      render: (value) => (
        <Tag color={value > 10 ? "red" : value > 5 ? "orange" : "green"}>
          {value}
        </Tag>
      ),
    },
    {
      title: t("store_skips"),
      dataIndex: "storeSkips",
      key: "storeSkips",
      width: 100,
      sorter: (a, b) => a.storeSkips - b.storeSkips,
      render: (value) => <Tag color="blue">{value}</Tag>,
    },
    {
      title: t("order_skips"),
      dataIndex: "orderSkips",
      key: "orderSkips",
      width: 100,
      sorter: (a, b) => a.orderSkips - b.orderSkips,
      render: (value) => <Tag color="orange">{value}</Tag>,
    },
    {
      title: t("total_trips"),
      dataIndex: "totalTrips",
      key: "totalTrips",
      width: 100,
      sorter: (a, b) => a.totalTrips - b.totalTrips,
    },
    {
      title: t("avg_skips_per_trip"),
      dataIndex: "avgSkipsPerTrip",
      key: "avgSkipsPerTrip",
      width: 120,
      sorter: (a, b) => a.avgSkipsPerTrip - b.avgSkipsPerTrip,
      render: (value) => value?.toFixed(2) || "0.00",
    },
    {
      title: t("last_skip_date"),
      dataIndex: "lastSkipDate",
      key: "lastSkipDate",
      width: 150,
      render: (date) =>
        date ? new Date(date).toLocaleDateString() : "-",
    },
    {
      title: t("actions"),
      key: "actions",
      width: 80,
      fixed: "right",
      render: (_, record) => (
        <Tooltip title={t("view_history")}>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() =>
              navigate(`/skip-analytics/driver/${record.driverId}`)
            }
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <Card className={styles.card} title={t("drivers_skip_stats")}>
      <div className={styles.table}>
        <Table
          rowKey="driverId"
          columns={columns}
          dataSource={driversStats?.data || []}
          loading={loading.includes("driversStats")}
          scroll={{ x: 1000 }}
          pagination={{
            current: driversQuery?.page,
            pageSize: driversQuery?.per_page,
            total: driversStats?.totalCount || 0,
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} ${t("of")} ${total}`,
            onChange: (page, pageSize) => {
              actions.setDriversQuery({
                ...driversQuery,
                page,
                per_page: pageSize,
              });
            },
          }}
        />
      </div>
    </Card>
  );
};

export default SkipDriversTable;
